import { PermissionConstants } from '../../shared/constants/permission-constants';

/** What a tab's visibility rule can look at — the same inputs the pages use today. */
export interface NavTabContext {
  /** Parsed localStorage 'account_settings' ({} when missing). */
  settings: Record<string, any>;
  /** True when the user has the permission (dataManager.permissionsArray). */
  can: (permission: string) => boolean;
  /** True when the active account is PayPal-boarded (BaseComponent.isPaypalAccount). */
  paypalAccount: boolean;
}

export interface NavTab {
  /** First route segment of the backend menu item that owns the tab, e.g. 'payment' for /payment. */
  parent: string;
  label: string;
  route: string;
  /** Omit when the page always shows the tab. */
  visible?: (ctx: NavTabContext) => boolean;
}

/*
 * In-page tabs of the screens the backend menu links to. The menu (/merchant/login `menu`) only
 * carries the top-level pages; their tabs are hardcoded in each module's shell template:
 *   payment.component.html, subscription.component.html, ach-reject.component.html,
 *   api.component.html, risks.component.html, balance.component.html.
 * Keep labels, routes and visibility rules in step with those templates (the spec checks each
 * route exists in its routing module). Shown by the topbar search only when the parent page is
 * in the user's menu.
 */
export const NAV_TABS: NavTab[] = [
  // payment.component.html (+ payment.component.ts ngOnInit for the conditions)
  { parent: 'payment', label: 'Card Transactions', route: '/payment/payment-list' },
  {
    parent: 'payment', label: 'PayPal Transactions', route: '/payment/paypal-transactions',
    visible: ctx => !!(ctx.settings.enable_paypal_payments || ctx.settings.enable_paypal_venmo || ctx.paypalAccount)
  },
  {
    parent: 'payment', label: 'ACH Transactions', route: '/payment/payment-ach-list',
    visible: ctx => !!ctx.settings.enable_ach_payments
  },
  {
    parent: 'payment', label: 'Batch Transactions', route: '/payment/batch-report',
    visible: ctx => !ctx.paypalAccount
  },
  {
    parent: 'payment', label: 'Authorization Log', route: '/payment/authorization-log',
    visible: ctx => ctx.can(PermissionConstants.PERMISSION_AUTHORIZATION_LOG)
  },
  { parent: 'payment', label: 'Exports', route: '/payment/payments-merchant' },
  { parent: 'payment', label: 'Invoices', route: '/payment/payments-invoice' },

  // subscription.component.html
  { parent: 'subscription', label: 'Subscriptions', route: '/subscription/subscription-list' },
  { parent: 'subscription', label: 'Plans', route: '/subscription/plan-list' },
  { parent: 'subscription', label: 'Discounts', route: '/subscription/discount-list' },
  { parent: 'subscription', label: 'Invoices', route: '/subscription/invoice-list' },
  { parent: 'subscription', label: 'Settings', route: '/subscription/setting' },

  // ach-reject.component.html
  { parent: 'ach-rejects', label: 'Ach Returns', route: '/ach-rejects/ach-rejects-list' },
  { parent: 'ach-rejects', label: 'Failed Re-Attempted Fees', route: '/ach-rejects/ach-represented' },
  { parent: 'ach-rejects', label: 'Paid ACH Returns', route: '/ach-rejects/ach-paid' },

  // api.component.html (navigates relative to /api via goNextTap)
  { parent: 'api', label: 'API', route: '/api/api-main' },
  { parent: 'api', label: 'Webhooks', route: '/api/webhooks' },
  { parent: 'api', label: 'Terminal Registry', route: '/api/terminal' },

  // risks.component.html
  { parent: 'risks', label: 'Limits', route: '/risks/limits' },
  { parent: 'risks', label: 'Reviews', route: '/risks/review' },

  // balance.component.html
  { parent: 'balance', label: 'Deposits summary', route: '/balance/deposits-report' },
  { parent: 'balance', label: 'Settings', route: '/balance/payout-settings' }
];

/** First path segment of a route, e.g. '/risks/limits' → 'risks', 'disputes/dispute-list' → 'disputes'. */
export function routeRoot(route: string): string {
  return (route || '').replace(/^\/+/, '').split(/[/?#]/)[0];
}
